import type {
  DiscordActivityCapabilities,
  DiscordActivityLinkedUser,
} from '@runhuman/shared';
import { TesterView } from './TesterView';
import { ActivityIdentityHeader } from './ActivityIdentityHeader';
import { InstanceParticipantsHeader } from './InstanceParticipantsHeader';
import { useInstanceParticipants } from '../use-instance-participants';
import { useFloorManagerOnDuty } from '../use-floor-manager-on-duty';
import { ActivityAudioProvider } from '../audio/audio-context';
import { ActivityAudioObserver } from '../audio/ActivityAudioObserver';
import { AudioMuteToggle } from '../audio/AudioMuteToggle';

interface Props {
  readonly discordUserId: string;
  readonly accessToken: string;
  readonly capabilities: DiscordActivityCapabilities;
  /**
   * Linked Runhuman identity — server-resolved alias (or email fallback) plus
   * the metadata needed to render the `<ActivityIdentityHeader />` strip.
   * Always set on this surface because `App.tsx` only reaches `<AuthorizedView>`
   * when the resolve contract carried a `linkedUser` payload (i.e. the
   * `view !== 'unlinked'` branch).
   */
  readonly linkedUser: DiscordActivityLinkedUser;
}

/**
 * Default landing surface for any linked user (tester / admin / admin-tester).
 *
 * Mounts the consolidated `<TesterView>` for everyone — admins see admin-only
 * widgets layered inline (header "Open jobs in admin" action + stuck-jobs
 * alert strip) rather than a separate Ops Console surface (#4172 retired the
 * flip). The queue is the at-a-glance landing for everyone; admins reach
 * deeper ops tools by popping into `/admin/*`.
 *
 * Audio cues live at this level so a single engine + mute state spans every
 * child surface. The `<ActivityAudioObserver>` is headless and subscribes to
 * the same React Query keys the views already use.
 *
 * Identity header sits above whichever child surface renders. The mute toggle
 * is a floating top-right button (its own positioning context) so the two
 * affordances do not compete for the same flow space.
 *
 * Co-presence header: admins/devs get an `<InstanceParticipantsHeader />`
 * pinned above the identity strip — sourced from the Discord SDK's instance-
 * participants channel, it shows who else is currently in this Activity
 * instance with the viewer. Scoped to `isAdmin`; the strip is non-essential
 * chrome and renders nothing when the participant list is empty, so the
 * hook's IPC handshake doesn't slow first paint of the view below it.
 */
export function AuthorizedView({
  discordUserId,
  accessToken,
  capabilities,
  linkedUser,
}: Props) {
  return (
    <ActivityAudioProvider discordUserId={discordUserId}>
      <ActivityAudioObserver accessToken={accessToken} />
      <AudioMuteToggle />
      {capabilities.isAdmin ? (
        <InstanceParticipantsHeaderConnected
          discordUserId={discordUserId}
          accessToken={accessToken}
        />
      ) : null}
      <ActivityIdentityHeader linkedUser={linkedUser} />
      <TesterView
        discordUserId={discordUserId}
        accessToken={accessToken}
        capabilities={capabilities}
      />
    </ActivityAudioProvider>
  );
}

/**
 * Thin connector that pulls live participants from the SDK and the on-duty
 * Floor Manager from the shared FM cache, then feeds them to the
 * presentational `<InstanceParticipantsHeader>`. Kept separate so the
 * presentational component remains hook-free and rendered directly from the
 * preview harness against fixture data.
 *
 * The FM lookup shares `useFloorManagerOnDuty`'s React Query cache with the
 * FM tile rendered inside `<TesterView>`, so the badge here and the tile
 * there always agree on who the on-duty FM is, with one underlying poll.
 */
function InstanceParticipantsHeaderConnected({
  discordUserId,
  accessToken,
}: {
  readonly discordUserId: string;
  readonly accessToken: string;
}) {
  const participants = useInstanceParticipants();
  const fmQuery = useFloorManagerOnDuty(accessToken);
  return (
    <InstanceParticipantsHeader
      participants={participants}
      currentUserId={discordUserId}
      floorManagerDiscordUserIds={resolveFloorManagerDiscordUserIds(fmQuery.data)}
    />
  );
}

/**
 * Collects the linked Discord ids of every on-duty FM (#5169) so the header
 * can badge each co-present FM chip. Handles the still-loading state (React
 * Query's `data` is `undefined`) as an empty list, and drops on-duty FMs who
 * haven't linked Discord (`discordUserId === null`) since they can't be matched
 * to a participant chip.
 */
function resolveFloorManagerDiscordUserIds(
  payload: { readonly onDuty: readonly { readonly discordUserId: string | null }[] } | undefined,
): string[] {
  if (!payload) return [];
  return payload.onDuty
    .map((fm) => fm.discordUserId)
    .filter((id): id is string => id !== null);
}
